<script setup lang="ts">
import { computed, nextTick, ref, useId, watch, type Component } from 'vue';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LazyTooltip } from '@/components/ui/tooltip';
import SlidersIcon from '@/components/icons/SlidersIcon.vue';
import ColumnSettingsInspectorContent from '@/components/ColumnSettingsInspectorContent.vue';
import {
    rememberDisplayMenuPage,
    rememberedDisplayMenuPage,
    type DisplayMenuPage,
} from '@/components/DataTable/displayMenuPageMemory';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * The list toolbar's "Display ▾" button and its menu. The menu holds what
 * sets how the list looks, in this order:
 *
 * 1. Layout, as a segmented control: Table and Cards, and Fixed Card and Map
 *    where the record type has them.
 * 2. Group by, as a select of the list's columns.
 * 3. "Columns…", with how many are shown. It slides the menu sideways to a
 *    Columns page, headed "‹ Columns · 6 shown". That page is the Columns
 *    panel, with its Shown and Hidden lists, search, drag to reorder, pins,
 *    sort and Reset. Every change applies at once, and nothing covers the
 *    list, so the person sees each change land. The ‹ button goes back.
 *    On the map the row reads "Map fields…" and its page picks the fields of
 *    the map's result cards.
 *
 * Each part shows only when its host passes it, so a page without layouts or
 * grouping, such as Track matching, gets a menu with "Columns…" alone.
 *
 * Closing the menu on the Columns page makes it reopen there, so getting back
 * to the columns is one click. Closing on the main page reopens on the main
 * page. Each list keeps its own choice. See displayMenuPageMemory.
 *
 * Escape on the Columns page goes back to the main page. A second Escape
 * closes the menu. Escape in the column search clears the search first.
 *
 * There is no Sort. The list's sort is a set of columns, each with its own
 * direction, and one select cannot show or set that.
 *
 * Spec: docs/specs/macro-style-layout.md, "Lists".
 */
export interface DisplayLayoutOption {
    value: string;
    label: string;
    icon: Component;
    /** The option's test id. The list toolbar keeps the ids its old view menu had. */
    testId: string;
}

export interface DisplayGroupByOption {
    value: string;
    label: string;
}

const props = withDefaults(
    defineProps<{
        /** The layouts to choose from. Fewer than two leaves Layout out. */
        layoutOptions?: DisplayLayoutOption[];
        layout?: string;
        /** The columns to group by. Null leaves Group by out. */
        groupByOptions?: DisplayGroupByOption[] | null;
        /** The column the list is grouped by, or null for no grouping. */
        groupBy?: string | null;
        /** The words of the Columns link, such as "Columns…". Null leaves it out. */
        columnsLabel?: string | null;
        /** How many columns are shown, for "6 shown". Null shows no count. */
        columnsShownCount?: number | null;
        /** Show the word "Display" and the chevron. False leaves the icon alone, with a tooltip. */
        showLabel?: boolean;
        /** The Columns link's test id. Each page keeps the id its old Columns button had. */
        columnsTestId?: string;
        /** The Columns page lists the map's card fields instead of the table's columns. */
        columnsMapMode?: boolean;
        /** Extra columns the Columns page can show or hide, such as the list's Play column. */
        columnsAdditionalColumns?: TableColumnStaticProps[];
        /** Which list this menu belongs to, so each list reopens on its own page. */
        pageMemoryKey?: string;
    }>(),
    {
        layoutOptions: () => [],
        layout: '',
        groupByOptions: null,
        groupBy: null,
        columnsLabel: null,
        columnsShownCount: null,
        showLabel: true,
        columnsTestId: 'display-columns',
        columnsMapMode: false,
        columnsAdditionalColumns: () => [],
        pageMemoryKey: 'list',
    },
);

const emit = defineEmits<{
    'update:layout': [value: string];
    'update:groupBy': [value: string | null];
}>();

const NO_GROUPING = '__none__';
const open = ref(false);
const layoutLabelId = useId();

const showsLayout = computed(() => props.layoutOptions.length > 1);

function onGroupByChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    emit('update:groupBy', value === NO_GROUPING ? null : value);
}

/** Choosing a layout closes the menu, because the whole list underneath changes. */
function chooseLayout(value: string) {
    open.value = false;
    emit('update:layout', value);
}

const page = ref<DisplayMenuPage>('main');
const columnsPageTitle = ref<HTMLElement | null>(null);
const columnsRow = ref<HTMLElement | null>(null);

/** The page's heading, such as "Columns" or "Map fields", from the row's words. */
const columnsPageName = computed(() => (props.columnsLabel ?? '').replace(/…$/, ''));

/**
 * The Columns page shows only while the host offers it. A list that last
 * closed on Columns, then moved to a layout without columns, opens on main.
 */
const showsColumnsPage = computed(() => page.value === 'columns' && props.columnsLabel !== null);

/**
 * Focus the Columns page's header after the slide in, or the "Columns…" row
 * after the slide back. It runs on nextTick for a page drawn at once, and again
 * from the transition's enter hook for a page that waited for the slide.
 */
function focusPageStart() {
    void nextTick(() => {
        if (showsColumnsPage.value) {
            columnsPageTitle.value?.focus();
        } else {
            columnsRow.value?.focus();
        }
    });
}

function goToPage(nextPage: DisplayMenuPage) {
    page.value = nextPage;
    rememberDisplayMenuPage(props.pageMemoryKey, nextPage);
    focusPageStart();
}

watch(open, (isOpen) => {
    if (isOpen) {
        page.value = rememberedDisplayMenuPage(props.pageMemoryKey);
    }
});

/** On a reopen straight onto the Columns page, focus its header, as a slide does. */
function onOpenAutoFocus(event: Event) {
    if (showsColumnsPage.value) {
        event.preventDefault();
        focusPageStart();
    }
}

/** Escape on the Columns page goes back first. Only Escape on the main page closes the menu. */
function onEscapeKeyDown(event: KeyboardEvent) {
    if (showsColumnsPage.value) {
        event.preventDefault();
        goToPage('main');
    }
}

const segmentClass =
    'flex min-w-0 flex-1 flex-col items-center gap-1 rounded-lg px-1 py-2 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-checked:bg-background aria-checked:font-medium aria-checked:text-foreground aria-checked:shadow-sm dark:aria-checked:bg-foreground/[0.12]';
</script>

<template>
  <!-- The tooltip names the button only while it shows no word. It wraps the whole
       popover, not just its trigger. See LazyTooltip for why. -->
  <LazyTooltip :enabled="!showLabel">
    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button
          variant="toolbar"
          size="sm"
          class="px-2"
          data-testid="display-menu-trigger"
          :aria-label="showLabel ? undefined : 'Display'"
        >
          <SlidersIcon class="size-4 shrink-0" />
          <template v-if="showLabel">
            <span>Display</span>
            <ChevronDownIcon class="size-3 shrink-0 opacity-60" aria-hidden="true" />
          </template>
        </Button>
      </PopoverTrigger>
      <!-- One width for both pages, so the menu does not jump when it slides.
           It never runs wider than the screen less an 8px gutter each side. The
           Columns page scrolls inside, up to the room below the button. -->
      <PopoverContent
        align="end"
        :side-offset="6"
        :collision-padding="8"
        class="flex max-h-[min(36rem,var(--reka-popover-content-available-height))] w-[min(340px,calc(100vw-16px))] flex-col overflow-hidden p-0"
        data-testid="display-menu"
        :data-page="showsColumnsPage ? 'columns' : 'main'"
        @open-auto-focus="onOpenAutoFocus"
        @escape-key-down="onEscapeKeyDown"
      >
        <!-- out-in mounts the next page only after the old one has left, so focus
             moves on @enter, once the page is there. -->
        <Transition
          :name="showsColumnsPage ? 'display-page-forward' : 'display-page-back'"
          mode="out-in"
          @enter="focusPageStart"
        >
          <div v-if="!showsColumnsPage" key="main" class="flex flex-col gap-3 p-3" data-testid="display-main-page">
            <div v-if="showsLayout">
              <p :id="layoutLabelId" class="mb-1.5 text-[11px] text-muted-foreground">Layout</p>
              <div
                role="radiogroup"
                :aria-labelledby="layoutLabelId"
                class="flex gap-1 rounded-xl bg-foreground/[0.06] p-1"
                data-testid="display-layout"
              >
                <button
                  v-for="option in layoutOptions"
                  :key="option.value"
                  type="button"
                  role="radio"
                  :aria-checked="layout === option.value"
                  :data-testid="option.testId"
                  :class="segmentClass"
                  @click="chooseLayout(option.value)"
                >
                  <component :is="option.icon" class="size-[18px] shrink-0" aria-hidden="true" />
                  <span class="max-w-full truncate">{{ option.label }}</span>
                </button>
              </div>
            </div>

            <label v-if="groupByOptions" class="flex h-8 items-center justify-between gap-3 text-sm">
              <span>Group by</span>
              <select
                class="h-7 min-w-0 max-w-44 truncate rounded-lg bg-foreground/[0.06] px-2 text-xs text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="display-group-by"
                :value="groupBy ?? NO_GROUPING"
                @change="onGroupByChange"
              >
                <option :value="NO_GROUPING">No grouping</option>
                <option v-for="option in groupByOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
            </label>

            <button
              v-if="columnsLabel"
              type="button"
              ref="columnsRow"
              class="-mx-1 flex h-8 items-center justify-between rounded-lg px-1 text-sm transition-colors hover:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              :data-testid="columnsTestId"
              @click="goToPage('columns')"
            >
              <span>{{ columnsLabel }}</span>
              <span class="flex items-center gap-1 text-xs text-muted-foreground">
                <span v-if="columnsShownCount !== null" data-testid="display-columns-count">{{ columnsShownCount }} shown</span>
                <ChevronRightIcon class="size-3.5" aria-hidden="true" />
              </span>
            </button>
          </div>

          <!-- The Columns page: the same panel the list had as a side peek. -->
          <div v-else key="columns" class="flex min-h-0 flex-1 flex-col" data-testid="display-columns-page">
            <div class="flex shrink-0 items-center gap-1 px-2 pb-1 pt-2">
              <button
                type="button"
                class="flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Back to Display"
                data-testid="display-columns-back"
                @click="goToPage('main')"
              >
                <ChevronLeftIcon class="size-4" aria-hidden="true" />
              </button>
              <h2
                ref="columnsPageTitle"
                tabindex="-1"
                class="flex min-w-0 flex-1 items-baseline gap-1.5 rounded text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="display-columns-page-title"
              >
                <span class="truncate">{{ columnsPageName }}</span>
                <span v-if="columnsShownCount !== null" class="shrink-0 text-xs font-normal text-muted-foreground">
                  · {{ columnsShownCount }} shown
                </span>
              </h2>
            </div>
            <ColumnSettingsInspectorContent
              class="min-h-0 flex-1"
              :map-mode="columnsMapMode"
              :additional-columns="columnsAdditionalColumns"
            />
          </div>
        </Transition>
      </PopoverContent>
    </Popover>
    <template #content>Display</template>
  </LazyTooltip>
</template>

<style scoped>
/* The Columns page slides in from the right, and slides back out to it. */
.display-page-forward-enter-active,
.display-page-forward-leave-active,
.display-page-back-enter-active,
.display-page-back-leave-active {
  transition: transform 140ms ease-out, opacity 140ms ease-out;
}
.display-page-forward-enter-from,
.display-page-back-leave-to {
  transform: translateX(24px);
  opacity: 0;
}
.display-page-forward-leave-to,
.display-page-back-enter-from {
  transform: translateX(-24px);
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .display-page-forward-enter-active,
  .display-page-forward-leave-active,
  .display-page-back-enter-active,
  .display-page-back-leave-active {
    transition: none;
  }
}
</style>
